import styled, { keyframes, css } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const slideUp = keyframes`
  from { 
    transform: translateY(100%);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
`;

// ============================================
// CONSTANTS
// ============================================

export const CALL_BAR_COLORS = {
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
  backgroundActive: 'linear-gradient(135deg, #0f0f0f 0%, #262626 50%, #0f0f0f 100%)',
  text: '#ffffff',
  textMuted: '#a0a0a0',
  success: '#52c41a',
  warning: '#faad14',
  danger: '#ff4d4f',
  primary: '#1890ff',
  border: 'rgba(255, 255, 255, 0.1)',
  buttonBg: 'rgba(255, 255, 255, 0.1)',
  buttonHover: 'rgba(255, 255, 255, 0.2)',
};

// ============================================
// MAIN CONTAINER
// ============================================

export const CallBarContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: ${CALL_BAR_COLORS.background};
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.3s ease-out;
  border-top: 1px solid ${CALL_BAR_COLORS.border};
  
  @media (max-width: 768px) {
    padding: 0 12px;
    height: 56px;
  }
`;

// ============================================
// LEFT SECTION - Status & Contact Info
// ============================================

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

export const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  
  ${props => {
    switch (props.$status) {
      case 'connected':
        return css`
          background: rgba(82, 196, 26, 0.2);
          color: ${CALL_BAR_COLORS.success};
          
          .status-dot {
            background: ${CALL_BAR_COLORS.success};
            animation: ${pulse} 1.5s ease-in-out infinite;
          }
        `;
      case 'ringing':
        return css`
          background: rgba(250, 173, 20, 0.2);
          color: ${CALL_BAR_COLORS.warning};
          
          .status-dot {
            background: ${CALL_BAR_COLORS.warning};
            animation: ${pulse} 0.8s ease-in-out infinite;
          }
        `;
      case 'initiating':
        return css`
          background: rgba(24, 144, 255, 0.2);
          color: ${CALL_BAR_COLORS.primary};
          
          .status-dot {
            background: ${CALL_BAR_COLORS.primary};
            animation: ${pulse} 0.5s ease-in-out infinite;
          }
        `;
      default:
        return css`
          background: rgba(255, 255, 255, 0.1);
          color: ${CALL_BAR_COLORS.text};
          
          .status-dot {
            background: ${CALL_BAR_COLORS.text};
          }
        `;
    }
  }}
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
`;

export const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ContactAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
`;

export const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  
  .contact-name {
    color: ${CALL_BAR_COLORS.text};
    font-weight: 600;
    font-size: 14px;
    line-height: 1.3;
  }
  
  .contact-numbers {
    color: ${CALL_BAR_COLORS.textMuted};
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    
    .arrow {
      font-size: 10px;
    }
  }
  
  @media (max-width: 768px) {
    .contact-name {
      font-size: 13px;
    }
    
    .contact-numbers {
      font-size: 11px;
    }
  }
`;

// ============================================
// CENTER SECTION - Timer
// ============================================

export const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 600px) {
    display: none;
  }
`;

export const TimerDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${CALL_BAR_COLORS.text};
  font-size: 16px;
  font-weight: 600;
  font-family: 'SF Mono', 'Consolas', monospace;
  
  svg {
    color: ${CALL_BAR_COLORS.textMuted};
    font-size: 14px;
  }
`;

// ============================================
// RIGHT SECTION - Action Buttons
// ============================================

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

export const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${CALL_BAR_COLORS.buttonBg};
  color: ${CALL_BAR_COLORS.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 16px;
  
  &:hover {
    background: ${CALL_BAR_COLORS.buttonHover};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &.active {
    background: ${CALL_BAR_COLORS.primary};
    color: white;
  }
  
  &.muted {
    background: rgba(255, 77, 79, 0.2);
    color: ${CALL_BAR_COLORS.danger};
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
`;

export const EndCallButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${CALL_BAR_COLORS.danger};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 16px;
  margin-left: 8px;
  
  &:hover {
    background: #ff7875;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
    margin-left: 4px;
  }
`;

// ============================================
// DIVIDER
// ============================================

export const Divider = styled.div`
  width: 1px;
  height: 28px;
  background: ${CALL_BAR_COLORS.border};
  margin: 0 8px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;
